type ActiveSection = {
  id: string;
  visibleHeight: number;
};

export function initPdpNavigation() {
  autoregisterPdpNavigation();
  updateCurrentSection();
  setupAnchorLinksHandlers();
  window.addEventListener('scroll', updateCurrentSection);
}

function autoregisterPdpNavigation() {
  const navigation = document.getElementById('in-pdp-navigation');

  // Check if the navigation has to be auto-registered
  if (!navigation || !navigation.classList.contains('auto-register-sections')) {
    return;
  }

  const sections = document.querySelectorAll('.pdp-section');

  if (sections.length === 0) {
    return;
  }

  const linksContainer = navigation.querySelector('.pdp-navigation__links');

  if (!linksContainer) {
    return;
  }

  // Each section should have an ID and a data attribute for the section name
  sections.forEach((section) => {
    const sectionId = section.getAttribute('id');
    const sectionName = section.getAttribute('data-section-name');

    if (sectionId && sectionName) {
      const link = document.createElement('li');
      link.classList.add('pdp-navigation__link');
      link.innerHTML = `<a href="#${sectionId}">${sectionName}</a>`;
      linksContainer?.appendChild(link);
    }
  });
}

function updateCurrentSection() {
  const currentSection = getCurrentSection();
  handleNavigationFade();
  markSectionAsActive(currentSection);
}

function handleNavigationFade() {
  const navigation = document.getElementById('in-pdp-navigation');
  const targetSection = document.getElementById('pdp-navigation-marker');

  if (!navigation || !targetSection) return;

  const rect = targetSection.getBoundingClientRect();

  if (rect.bottom < 0) {
    navigation.style.transition = 'opacity 0.3s';
    navigation.style.opacity = '1';
    navigation.style.pointerEvents = 'auto';
  } else {
    navigation.style.transition = 'opacity 0.3s';
    navigation.style.opacity = '0';
    navigation.style.pointerEvents = 'none';

    const links = document.getElementById('in-pdp-navigation-links');

    if (links) {
      links.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
  }
}

function setupAnchorLinksHandlers() {
  const anchorLinks = document.querySelectorAll(
    '.pdp-navigation__link a[href^="#"]'
  );
  const OFFSET = 16; // Additional offset for better visibility
  const totalOffset = calculateHeaderOffset() + OFFSET;

  anchorLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const sectionId = link.getAttribute('href')?.substring(1);
      if (sectionId) {
        scrollToSection(sectionId, totalOffset);
        markSectionAsActive({ id: sectionId, visibleHeight: 0 });
      } else {
        // Means the link is pointing to the top of the page
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        markSectionAsActive(null);
      }
    });
  });
}

function calculateHeaderOffset() {
  const header = document.getElementById('shopify-section-header');
  const navigation = document.getElementById('in-pdp-navigation');

  let headerOffset = 0;

  if (header) {
    headerOffset += header.getBoundingClientRect().height;
  }

  if (navigation) {
    headerOffset += navigation.getBoundingClientRect().height;
  }

  return headerOffset;
}

function scrollToSection(sectionId: string, offset = 0) {
  const section = document.getElementById(sectionId);

  if (section) {
    const sectionTop =
      section.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top: sectionTop,
      behavior: 'smooth',
    });
  }
}

function markSectionAsActive(section?: ActiveSection | null) {
  const activeLink = document.querySelector(
    '.pdp-navigation__link.pdp-navigation__link--active'
  );

  // if the active link is already active, do nothing
  if (
    activeLink &&
    section &&
    activeLink.querySelector('a')?.getAttribute('href') === `#${section.id}`
  ) {
    return;
  }

  if (activeLink) {
    activeLink.classList.remove('pdp-navigation__link--active');
  }

  if (!section) {
    return;
  }

  const newActiveLink = document.querySelector(
    `.pdp-navigation__link a[href="#${section.id}"]`
  );

  if (newActiveLink && section.visibleHeight > 0) {
    newActiveLink.parentElement?.classList.add('pdp-navigation__link--active');
    newActiveLink.setAttribute('aria-current', 'true');

    scrollToActiveLink(newActiveLink);
  }
}

function scrollToActiveLink(activeLink: Element) {
  const navigation = document.getElementById('in-pdp-navigation-links');

  if (!navigation) {
    return;
  }

  const linkRect = activeLink.getBoundingClientRect();
  const navRect = navigation.getBoundingClientRect();

  // Calculate the amount to scroll so the active link is flush with the left edge
  // But clamp so we don't scroll past the max scrollable area
  const scrollLeft = navigation.scrollLeft + (linkRect.left - navRect.left);

  const maxScrollLeft = navigation.scrollWidth - navigation.clientWidth;
  const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));

  // Check if the active link's parent is the last child among its siblings
  const parent = activeLink.parentElement;
  const isLastChild = parent
    ? parent === parent.parentElement?.lastElementChild
    : false;

  navigation.scrollTo({
    left: isLastChild ? clampedScrollLeft : Math.max(0, clampedScrollLeft - 32),
    behavior: 'smooth',
  });
}

/**
 * Gets the current section based on the scroll position.
 * Iterates through all sections with the class 'pdp-section'.
 * Determines which section is currently most visible based on the scroll position.
 *
 * @returns {ActiveSection|null} An object containing the section ID and its visible height, or null if no section is visible.
 * @property {string} id - The ID of the currently visible section.
 * @property {number} visibleHeight - The height of the section that is currently visible in the viewport.
 */
function getCurrentSection(): ActiveSection | null {
  const sections = document.querySelectorAll('.pdp-section');
  let currentSection: ActiveSection | null = null;
  let maxVisiblePercentage = 0;

  for (const section of sections) {
    const rect = (section as HTMLElement).getBoundingClientRect();
    const sectionHeight = rect.height;
    if (sectionHeight === 0) continue;

    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    const visiblePercentage = visibleHeight / sectionHeight;

    if (visiblePercentage > maxVisiblePercentage) {
      maxVisiblePercentage = visiblePercentage;
      currentSection = { id: section.id, visibleHeight };
    }
  }

  return currentSection;
}
